import { createStore } from 'zustand/vanilla'
import gameManager from '@/ui/gameManager'
import { toCurrency } from '@/ui/currency'
import { getUrlParams } from '@/ui/url'
import { preloadRepo } from './preload'
import { navRepo } from './nav'
import { dialogRepo } from './dialog'
import { configRepo } from './config'
import { notificationRepo } from './notification'
import { infoRepo, updatePayout } from './info'
import { fetchURLAPI } from '@/ui/http'
import { getAssetUrl } from '@/ui/assets'
import { setAppStyle } from '@/ui/style'
import { ModulesManager } from '@modules/src/managers/ModulesManager'
import { TranslationsModule } from '@modules/src/modules/TranslationsModule'

const normalizeLocale = (value?: string) =>
    String(value || 'en')
        .trim()
        .toLowerCase()
        .split('-')[0]

const updateRuntimeLocale = async (value?: string) => {
    try {
        const translationsModule = ModulesManager.getModule('translations') as TranslationsModule
        await translationsModule?.setLocale?.(normalizeLocale(value))
    } catch (error) {
        console.warn('failed to update runtime locale', error)
    }
}

export interface IGameState {
    game_version: string
    casino_token: string
    game_code: string
    balance: number | string
    currency_symbol: string
    auto_play_arr: number[]
    stop_balance_low_arr: number[]
    stop_balance_high_arr: number[]
    bets_arr: number[]
    current_bet: number
    current_base_bet: number
    current_auto_play: number
    current_stop_single_win: number
    current_stop_balance_low: number
    current_stop_balance_high: number
    sound: boolean
    music: boolean
    spacebar_spin: boolean
    home_url?: string
    stop_min_single_win?: number
    stop_max_single_win?: number
    country: string
    language: string
    win_info?: null | string
    info_text?: null | string
    info_text_size?: null | number
    stop_spin?: boolean
    payout: null | Record<string, number[]>
    autoplay_enabled?: boolean
    quickspin_enabled?: boolean
    show_win_history?: boolean
    show_lobby_button?: boolean
    show_home_button?: boolean
    rtp: null | number
    rtp_max?: null | number
    rtp_min?: null | number
    max_win_text: null | string
    display_max_win?: boolean
    bonus_rtp: null | number
    super_bonus_rtp: null | number
    premium_rtp: null | number
    max_bet: null | number
    bonus_buy_enabled: null | boolean
    bonus_buy_text_visible: null | boolean
    premium_spin_text_visible: null | boolean
    bonus_buy_items: {
        id: number
        multiplier: number
        title?: string
        btn_text?: string
        src?: string
        width?: string
        height?: string
        desc?: string
    }[]
    bonus_mode: null | boolean
    show_spacebar: null | boolean
    notification_server_url: null | string
    notification_server_token: null | string
    turbo_spin_enabled: boolean
    turbo_spin: boolean
    show_splash_toggle_enabled: boolean
    show_splash: boolean
    win_up_to: null | number
}

export type TButtonMod =
    | 'autoplay'
    | 'autoplay_stop'
    | 'bet'
    | 'play'
    | 'stop'
    | 'menu'
    | 'info'
    | 'bonus_buy'
    | 'bonus_mode_stop'
    | 'turbo_spin'

export interface IGameRepo extends IGameState {
    state: 'not_ready' | 'ready' | 'playing'
    mods: {
        [key in TButtonMod]?: 'enabled' | 'disabled' | 'pressed'
    }
    gameLoading: number
    isGameLoaded: boolean

    send: typeof gameManager.send

    btnOver: (button_name: string) => void
    btnDown: (button_name: string) => void
    toggleMute: () => void
    toggleTurboSpin: () => void
    betByMode: (mode: number) => void
    injectBonusBuyData: (bonus_links: Record<string, string>, base_assets_url?: string) => Promise<void>
}

export const gameRepo = createStore<IGameRepo>((set, get) => {
    const urlParams = getUrlParams()

    return {
        state: 'not_ready',
        mods: {},
        gameLoading: 0,
        isGameLoaded: false,

        balance: 0,
        game_code: '',
        casino_token: '',
        game_version: '1.0.0',
        currency_symbol: '',
        home_url: '',
        auto_play_arr: [],
        stop_min_single_win: 0,
        stop_max_single_win: 0,
        stop_balance_low_arr: [],
        stop_balance_high_arr: [],
        bets_arr: [],
        current_bet: 0,
        current_base_bet: 0,
        current_auto_play: 0,
        current_stop_single_win: 0,
        current_stop_balance_low: 0,
        current_stop_balance_high: 0,
        sound: true,
        music: true,
        spacebar_spin: false,
        country: '',
        language: urlParams.language || 'en',
        stop_spin: true,
        payout: null,
        autoplay_enabled: false,
        quickspin_enabled: false,
        show_win_history: false,
        show_lobby_button: false,
        show_home_button: false,
        rtp: null,
        rtp_max: null,
        rtp_min: null,
        bonus_rtp: null,
        super_bonus_rtp: null,
        premium_rtp: null,
        max_win_text: null,
        display_max_win: false,
        max_bet: null,
        bonus_buy_enabled: null,
        bonus_buy_text_visible: null,
        premium_spin_text_visible: null,
        bonus_buy_items: [],
        bonus_mode: null,
        show_spacebar: null,
        notification_server_url: null,
        notification_server_token: null,
        turbo_spin_enabled: false,
        turbo_spin: false,
        show_splash_toggle_enabled: false,
        show_splash: false,

        send: gameManager.send,

        win_up_to: null,

        btnOver: (name) =>
            gameManager.send('hover_button', {
                name,
            }),

        btnDown: (name) =>
            gameManager.send('clicked_button', {
                name,
            }),

        toggleMute: () => {
            const { music, sound } = get()
            const status = !(music || sound)

            gameManager.send('set_sound', { value: status })
            gameManager.send('set_music', { value: status })
        },

        injectBonusBuyData: async (bonus_links, base_assets_url) => {
            const { game_code, bonus_buy_items } = get()

            let data: { items?: any } = {}

            if (bonus_links[game_code]) {
                data = await fetchURLAPI(getAssetUrl(bonus_links[game_code], base_assets_url))
            } else if (bonus_links?.default) {
                data = await fetchURLAPI(getAssetUrl(bonus_links.default, base_assets_url))
            }

            set({
                bonus_buy_items: bonus_buy_items.map((item) => {
                    const found = data?.items?.find((it: any) => it.id === item.id) || {}

                    return { ...found, ...item }
                }),
            })
        },

        betByMode: (mode: number) => {
            const { bets_arr, current_bet, send } = get()
            const current = bets_arr.findIndex((bet) => bet === current_bet)

            if (bets_arr[current + mode]) {
                send('set_bet', {
                    value: bets_arr[current + mode],
                })
            }
        },

        toggleTurboSpin: () => {
            const { turbo_spin, send } = get()

            send('set_turbo_spin', {
                value: !turbo_spin,
            })
        },
    }
})

// Game manager subscribes

gameManager.subscribe('loading', ({ value }) => {
    preloadRepo.getState().setProgress('gameFiles', value)

    gameRepo.setState({
        gameLoading: value || 0,
    })
})

gameManager.subscribe('game_ready', () => {
    const preload = preloadRepo.getState()
    preload.finish('gameFiles')
    preload.setProgress('gameFiles', 100)
    preloadRepo.setState({
        isActive: false,
        progress: 100,
    })

    gameRepo.setState({
        isGameLoaded: true,
    })
})

gameManager.subscribe('show_ui', ({ value, animation_delay = 0, animation_duration = 0 }) => {
    configRepo.setState({
        showUI: !!value,
        showUIdelay: animation_delay,
        showUIduration: animation_duration,
    })
})

gameManager.subscribe('balance', ({ value }) => {
    gameRepo.setState({
        balance: (Number(value) || 0).toFixed(2),
    })
})

gameManager.subscribe('bet', ({ value, base_value }) => {
    gameRepo.setState({
        current_base_bet: base_value || value || 0,
        current_bet: value || 0,
    })
})

gameManager.subscribe('bets_arr', ({ arr, value }) => {
    gameRepo.setState({
        bets_arr: arr || [],
        current_bet: value || 0,
    })
})

gameManager.subscribe('autoplay', ({ value }) => {
    gameRepo.setState({
        current_auto_play: value || 0,
    })
})

gameManager.subscribe('stop_single_win', ({ value }) => {
    gameRepo.setState({
        current_stop_single_win: value || 0,
    })
})

gameManager.subscribe('stop_balance_lower', ({ value }) => {
    gameRepo.setState({
        current_stop_balance_low: value || 0,
    })
})

gameManager.subscribe('stop_balance_higher', ({ value }) => {
    gameRepo.setState({
        current_stop_balance_high: value || 0,
    })
})

gameManager.subscribe('spacebar_spin', ({ value }) => {
    gameRepo.setState({
        spacebar_spin: !!value,
    })
})

gameManager.subscribe('sound', ({ value }) => {
    gameRepo.setState({
        sound: !!value,
    })
})

gameManager.subscribe('music', ({ value }) => {
    gameRepo.setState({
        music: !!value,
    })
})

gameManager.subscribe('language', ({ value }) => {
    if (!value) return

    const normalizedLanguage = normalizeLocale(value)

    void (async () => {
        await updateRuntimeLocale(normalizedLanguage)

        gameRepo.setState({
            language: normalizedLanguage,
        })
    })()
})

gameManager.subscribe('game_state', ({ state, mods = {} }) => {
    gameRepo.setState({
        state,
        mods,
    })
})

gameManager.subscribe('show_dialog', (data) => {
    // TODO: Warning info for Modal
    if (data) dialogRepo.getState().add(data)
})

gameManager.subscribe('update_info_text', ({ text, size }) => {
    gameRepo.setState({
        info_text: text,
        info_text_size: size,
    })
})

gameManager.subscribe('update_win_info', ({ text }) => {
    gameRepo.setState({
        win_info: text,
    })
})

gameManager.subscribe('stop_spin', ({ value }) => {
    gameRepo.setState({
        stop_spin: value,
    })
})

gameManager.subscribe('bonus_mode', ({ value }) => {
    gameRepo.setState({
        bonus_mode: !!value,
    })
})

gameManager.subscribe('close_dialog', ({ key }) => {
    dialogRepo.getState().closeByKey(key)
})

gameManager.subscribe('show_menu', ({ nav, id }) => {
    navRepo.getState().openNav(nav, id)
})

gameManager.subscribe('close_menu', () => {
    navRepo.getState().toggleUI({ forceState: false })
})

gameManager.subscribe('turbo_spin', ({ value }) => {
    gameRepo.setState({
        turbo_spin: value,
    })
})

gameManager.subscribe('show_splash', ({ value }) => {
    gameRepo.setState({
        show_splash: value,
    })
})

gameManager.subscribe('change_background', (value) => {
    setAppStyle(value)
})

gameManager.subscribe('init', (state) => {
    console.log('init - - - - - - - - - - - - >>', state)

    const initLanguage = normalizeLocale(gameRepo.getState().language || state.language)
    void (async () => {
        await updateRuntimeLocale(initLanguage)

        gameRepo.setState({
            balance: (state.balance || 0).toFixed(2),
            game_code: state.game_code || '',
            casino_token: state.casino_token || '',
            game_version: state.game_version || '1.0.0',
            currency_symbol: toCurrency(state.currency_symbol || ''),
            home_url: state.home_url || '',
            auto_play_arr: state.auto_play_arr || [],
            stop_min_single_win: state.stop_min_single_win || 0,
            stop_max_single_win: state.stop_max_single_win || 0,
            stop_balance_low_arr: state.stop_balance_low_arr || [],
            stop_balance_high_arr: state.stop_balance_high_arr || [],
            bets_arr: state.bets_arr || [],
            current_bet: state.current_bet || 0,
            current_base_bet: state.current_base_bet || state.current_bet || 0,
            current_auto_play: state.current_auto_play || 0,
            current_stop_single_win: state.current_stop_single_win || 0,
            current_stop_balance_low: state.current_stop_balance_low || 0,
            current_stop_balance_high: state.current_stop_balance_high || 0,
            sound: !!state.sound,
            music: !!state.music,
            spacebar_spin: !!state.spacebar_spin,
            country: state.country || 'en',
            language: initLanguage,
            payout: state.payout || null,
            autoplay_enabled: !!state?.autoplay_enabled,
            quickspin_enabled: !!state?.quickspin_enabled,
            show_win_history: !!state?.show_win_history,
            show_lobby_button: !!state?.show_lobby_button,
            show_home_button: !!state?.show_home_button,
            rtp: state.rtp || null,
            rtp_max: state.rtp_max || null,
            rtp_min: state.rtp_min || null,
            bonus_rtp: state.bonus_rtp || null,
            super_bonus_rtp: state.super_bonus_rtp || null,
            premium_rtp: state.premium_rtp || null,
            max_win_text: state.max_win_text || null,
            display_max_win: state.display_max_win,
            max_bet: state.max_bet || null,
            bonus_buy_enabled: !!state.bonus_buy_enabled,
            bonus_buy_text_visible: !!state.bonus_buy_text_visible,
            premium_spin_text_visible: !!state.premium_spin_text_visible,
            bonus_buy_items: state.bonus_buy_items || [],
            show_spacebar: !!state.show_spacebar,
            notification_server_url: state.notification_server_url || null,
            notification_server_token: state.notification_server_token || null,
            turbo_spin_enabled: !!state.turbo_spin_enabled,
            show_splash_toggle_enabled: !!state.show_splash_toggle_enabled,
            show_splash: !!state.show_splash,
            win_up_to: state.win_up_to || null,
        })
    })()

    // Initial connection
    if (state?.notification_server_token && state?.notification_server_url) {
        notificationRepo.getState().initConnect()
    }

    preloadRepo.getState().loadConfigItems()
})

gameRepo.subscribe(({ home_url }, prev) => {
    if (home_url === prev?.home_url) return

    navRepo.getState().updateNav('home', {
        isHide: !home_url,
    })
})

gameRepo.subscribe(({ autoplay_enabled }, prev) => {
    if (autoplay_enabled === prev?.autoplay_enabled) return

    navRepo.getState().updateNav('autoplay', {
        isHide: !autoplay_enabled,
    })
})

gameRepo.subscribe(({ bonus_buy_enabled }, prev) => {
    if (bonus_buy_enabled === prev?.bonus_buy_enabled) return

    navRepo.getState().updateNav('bonus_buy', {
        isHide: !bonus_buy_enabled,
    })
})

gameRepo.subscribe(({ current_base_bet }, prev) => {
    if (current_base_bet === prev?.current_base_bet) return

    infoRepo.setState(({ data }) => ({
        data: updatePayout(data),
    }))
})

gameRepo.subscribe(
    (
        { show_win_history, show_lobby_button, show_home_button, notification_server_token, notification_server_url },
        prev
    ) => {
        if (show_win_history !== prev?.show_win_history) {
            navRepo.getState().updateNav('win_history', {
                isHide: !show_win_history,
            })
        }

        if (show_lobby_button !== prev?.show_lobby_button) {
            navRepo.getState().updateNav('lobby', {
                isHide: !show_lobby_button,
            })
        }

        if (show_home_button !== prev?.show_home_button) {
            navRepo.getState().updateNav('home', {
                isHide: !show_home_button,
            })
        }

        if (
            notification_server_token !== prev?.notification_server_token ||
            notification_server_url !== prev?.notification_server_url
        ) {
            navRepo.getState().updateNav('tournaments', {
                isHide: !notification_server_token || !notification_server_url,
            })
        }
    }
)
